const HOME_SECTIONS = new Map([
  ["latest", "latest"],
  ["myhubs", "my-spaces"],
  ["hubs", "explore-spaces"],
]);

const SPACE_SECTIONS = new Set(["discussions", "chronicles", "members", "info"]);
const LEGACY_THREAD_ID = /^[a-z0-9_-]{1,128}$/i;

function readSearchParams(searchParams) {
  if (!searchParams) return new URLSearchParams();
  if (typeof searchParams === "string") {
    return new URLSearchParams(searchParams.replace(/^\?/, ""));
  }
  if (typeof searchParams.get === "function") return searchParams;
  return new URLSearchParams(String(searchParams));
}

function uniqueValues(searchParams, key) {
  const values = typeof searchParams.getAll === "function"
    ? searchParams.getAll(key)
    : [searchParams.get(key)];
  return [...new Set(values.map((value) => String(value || "").trim().toLowerCase()).filter(Boolean))];
}

function selectKnownValue(searchParams, key, allowed) {
  const values = uniqueValues(searchParams, key).filter((value) => allowed.has(value));
  return values.length === 1 ? values[0] : "";
}

function safeSegment(value, fallback = "unknown") {
  let decoded = "";
  try {
    decoded = decodeURIComponent(String(value || ""));
  } catch {
    return fallback;
  }

  const normalized = decoded
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");
  return normalized || fallback;
}

function hasThreadFocus(searchParams) {
  return uniqueValues(searchParams, "focus").some((value) => {
    if (!value.startsWith("thread:")) return false;
    return LEGACY_THREAD_ID.test(value.slice("thread:".length));
  });
}

function hasLegacyThreadQuery(searchParams) {
  const values = uniqueValues(searchParams, "t");
  return values.length === 1 && LEGACY_THREAD_ID.test(values[0]);
}

/**
 * Builds Sproutly's stable, identifier-free analytics hierarchy.
 *
 * The first segment identifies the resolved entry surface (`sproutly` or a
 * publisher portal ID). Remaining segments use product language instead of
 * literal URLs. Only recognized navigation parameters affect the result;
 * user/content identifiers and unrelated query parameters are discarded.
 */
export function buildAnalyticsContext({ pathname = "/", searchParams, surfaceId = "sproutly" } = {}) {
  const search = readSearchParams(searchParams);
  const cleanPath = String(pathname || "/").split(/[?#]/, 1)[0] || "/";
  const rawSegments = cleanPath.split("/").filter(Boolean);
  const segments = rawSegments.map((segment) => safeSegment(segment));
  const surface = safeSegment(surfaceId, "sproutly");
  const context = (value) => `/${surface}${value}`;

  if (segments.length === 0) {
    const tValues = uniqueValues(search, "t");
    const requested = tValues.length > 0
      ? selectKnownValue(search, "t", new Set(HOME_SECTIONS.keys()))
      : selectKnownValue(search, "tab", new Set(HOME_SECTIONS.keys()));
    return context(`/home/${HOME_SECTIONS.get(requested) || "latest"}`);
  }

  if (segments[0] === "h" && rawSegments[1]) {
    const spaceSlug = safeSegment(rawSegments[1]);
    const routeSection = segments[2] || "";

    if (routeSection === "discussions") {
      if (rawSegments[3] || hasLegacyThreadQuery(search)) {
        return context(`/spaces/${spaceSlug}/discussions/thread`);
      }
      return context(`/spaces/${spaceSlug}/discussions`);
    }

    if (routeSection === "chronicles") {
      if (rawSegments[3] && segments[4] === "incidents" && rawSegments[5]) {
        return context(`/spaces/${spaceSlug}/chronicles/chronicle/incident`);
      }
      if (rawSegments[3]) return context(`/spaces/${spaceSlug}/chronicles/chronicle`);
      return context(`/spaces/${spaceSlug}/chronicles`);
    }

    if (routeSection === "members" || routeSection === "info") {
      return context(`/spaces/${spaceSlug}/${routeSection}`);
    }

    if (hasThreadFocus(search)) return context(`/spaces/${spaceSlug}/discussions/thread`);
    const section = selectKnownValue(search, "t", SPACE_SECTIONS) || "discussions";
    return context(`/spaces/${spaceSlug}/${section}`);
  }

  if (segments[0] === "new-hub" && segments.length === 1) return context("/spaces/create");
  if (segments[0] === "u" && rawSegments[1]) return context("/profiles/member");
  if (segments[0] === "agent" && rawSegments[1]) return context("/profiles/agent");

  if (segments[0] === "profile") {
    if (segments[1] === "password") return context("/account/security");
    if (segments[1] === "setup") return context("/account/setup");
    if (segments[1] === "agents" && segments[2] && segments[3] === "tasks" && segments[4]) {
      return context("/account/agents/task");
    }
    if (segments[1] === "agents" && segments[2]) return context("/account/agents/detail");
    if (segments[1] === "agents") return context("/account/agents");
    return context("/account/profile");
  }

  if (segments[0] === "login") return context("/authentication/login");
  if (segments[0] === "signup" && segments[1] === "register") return context("/authentication/register");
  if (segments[0] === "signup") return context("/authentication/signup");

  if (segments[0] === "info" && segments[1] === "privacy") return context("/information/privacy");
  if (segments[0] === "info" && segments[1] === "terms") return context("/information/terms");
  if (segments[0] === "info" && segments[1] === "how-to") return context("/information/how-to");
  if (segments[0] === "info") return context("/information/about");
  if (segments[0] === "offline") return context("/system/offline");

  if (segments[0] === "dev") return context("/system/development");
  if (segments[0] === "api") return context("/system/api");
  if (segments[0] === "share") return context("/system/share");
  return context("/system/unknown");
}
